import { currentUser, editingUserName, set, usersCache } from '../../state';
import { callApi } from '../api';
import { showAuthPopup } from '../auth';
import { closeModal, esc, toast } from '../ui';
import { loadTopics, renderAdminTopicList } from './topics';
import { initConnectionSection } from './connection';
import { loadLineConfig } from './line';
import { initSheetsSection } from './sheets';

export async function initAdminPage(): Promise<void> {
  await loadTopics();
  renderAdminTopicList();
  switchAdminSection('users');
  const connInp = document.getElementById('connUrlInp') as HTMLInputElement | null;
  if (connInp) {
    const { apiUrl } = await import('../../state');
    connInp.value = apiUrl;
  }
  await loadUsers();
}

export function switchAdminSection(sec: string): void {
  document.querySelectorAll('.admin-nav-item').forEach(b =>
    b.classList.toggle('active', (b as HTMLElement).dataset['sec'] === sec));
  ['users', 'topics', 'line', 'connection', 'sheets'].forEach(s => {
    const el = document.getElementById(`adminSec${s.charAt(0).toUpperCase() + s.slice(1)}`);
    if (el) el.style.display = s === sec ? 'block' : 'none';
  });
  if (sec === 'connection') initConnectionSection();
  if (sec === 'line') loadLineConfig();
  if (sec === 'sheets') initSheetsSection();
}

export async function loadUsers(): Promise<void> {
  document.getElementById('usersTableWrap')!.innerHTML = '<div class="empty"><i class="ti ti-loader spin"></i>กำลังโหลด...</div>';
  const data = await callApi({ action: 'listUsers' });
  if (data.ok) {
    set.usersCache((data['users'] as typeof usersCache) ?? []);
    renderUsersTable();
  } else {
    const el = document.getElementById('usersTableWrap')!;
    el.innerHTML = '';
    const d = document.createElement('div'); d.className = 'empty';
    d.textContent = data.error ?? 'โหลดไม่สำเร็จ';
    el.appendChild(d);
  }
}

export function renderUsersTable(): void {
  if (!usersCache.length) {
    document.getElementById('usersTableWrap')!.innerHTML = '<div class="empty">ยังไม่มีผู้ใช้งาน</div>';
    return;
  }
  document.getElementById('usersTableWrap')!.innerHTML = `
    <table class="users-table">
      <thead><tr>
        <th>ผู้ใช้งาน</th><th>Username</th><th>Role</th><th>สถานะ</th><th>จัดการ</th>
      </tr></thead>
      <tbody>
        ${usersCache.map(u => {
          const isSelf = u.username === currentUser?.username;
          const avatarCls = u.role === 'admin' ? 'ua-admin' : 'ua-hr';
          const initial = esc((u.displayName || u.username).charAt(0).toUpperCase());
          return `<tr>
            <td>
              <div style="display:flex;align-items:center;gap:10px">
                <div class="user-avatar ${avatarCls}">${initial}</div>
                <div>
                  <div style="font-weight:600">${esc(u.displayName)}</div>
                  ${isSelf ? '<span class="self-badge">บัญชีของคุณ</span>' : ''}
                </div>
              </div>
            </td>
            <td style="color:var(--muted);font-family:monospace;font-size:13px">${esc(u.username)}</td>
            <td><span class="role-badge ${u.role === 'admin' ? 'rb-admin' : 'rb-hr'}">${u.role === 'admin' ? 'Admin' : 'HR Staff'}</span></td>
            <td><span class="status-badge ${u.active !== false ? 'sb-active' : 'sb-inactive'}"></span>${u.active !== false ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}</td>
            <td>
              <div class="user-actions">
                <button class="icon-btn" onclick="editUser('${esc(u.username)}')" title="แก้ไข"><i class="ti ti-edit"></i></button>
                <button class="icon-btn danger" onclick="deleteUser('${esc(u.username)}')" title="ลบ" ${isSelf ? 'disabled' : ''}><i class="ti ti-trash"></i></button>
              </div>
            </td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>`;
}

export function showUserForm(username: string | null = null): void {
  set.editingUserName(username);
  const isEdit = username !== null;
  const u = isEdit ? usersCache.find(x => x.username === username) : null;
  const title = document.getElementById('userModalTitle')!;
  title.innerHTML = `<i class="ti ${isEdit ? 'ti-edit' : 'ti-user-plus'}" style="color:var(--primary)"></i>`;
  title.appendChild(document.createTextNode(` ${isEdit ? 'แก้ไขผู้ใช้' : 'เพิ่มผู้ใช้งาน'}`));
  (document.getElementById('uUsername') as HTMLInputElement).value = u?.username ?? '';
  (document.getElementById('uUsername') as HTMLInputElement).disabled = isEdit;
  (document.getElementById('uDisplayName') as HTMLInputElement).value = u?.displayName ?? '';
  (document.getElementById('uRole') as HTMLSelectElement).value = u?.role ?? 'hr';
  (document.getElementById('uPassword') as HTMLInputElement).value = '';
  (document.getElementById('uActive') as HTMLInputElement).checked = u?.active !== false;
  document.getElementById('uPasswordLabel')!.textContent = isEdit ? 'รหัสผ่านใหม่ (เว้นว่างถ้าไม่เปลี่ยน)' : 'รหัสผ่าน *';
  (document.getElementById('uEditNote') as HTMLElement).style.display = isEdit ? 'block' : 'none';
  document.getElementById('uErr')!.textContent = '';
  document.getElementById('userModal')!.classList.add('open');
  setTimeout(() => {
    const focus = !isEdit ? document.getElementById('uUsername') : document.getElementById('uDisplayName');
    (focus as HTMLInputElement | null)?.focus();
  }, 120);
}

export function editUser(username: string): void { showUserForm(username); }

export function deleteUser(username: string): void {
  showAuthPopup('warning', `ลบผู้ใช้ "${username}"`, 'การดำเนินการนี้ไม่สามารถย้อนกลับได้', [
    { label: 'ยกเลิก', cls: 'neutral', cb: null },
    { label: 'ลบออก', cls: 'danger', cb: async () => {
      const data = await callApi({ action: 'deleteUser', username });
      if (data.ok) { toast('success', 'ลบผู้ใช้แล้ว'); await loadUsers(); }
      else toast('error', data.error ?? 'ลบไม่สำเร็จ');
    }},
  ]);
}

export async function submitUserForm(): Promise<void> {
  const username    = (document.getElementById('uUsername')    as HTMLInputElement).value.trim();
  const displayName = (document.getElementById('uDisplayName') as HTMLInputElement).value.trim();
  const role        = (document.getElementById('uRole')        as HTMLSelectElement).value;
  const password    = (document.getElementById('uPassword')    as HTMLInputElement).value;
  const active      = (document.getElementById('uActive')      as HTMLInputElement).checked;
  const errEl       = document.getElementById('uErr')!;

  if (!displayName) { errEl.textContent = 'กรุณาใส่ชื่อที่แสดง'; return; }
  if (!editingUserName && !username)  { errEl.textContent = 'กรุณาใส่ username'; return; }
  if (!editingUserName && !password)  { errEl.textContent = 'กรุณาใส่รหัสผ่าน'; return; }
  if (!editingUserName && password.length < 8) { errEl.textContent = 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร'; return; }
  if (editingUserName && password && password.length < 8) { errEl.textContent = 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร'; return; }

  const data = editingUserName
    ? await callApi({ action: 'updateUser', username: editingUserName, displayName, role, password: password || undefined, active })
    : await callApi({ action: 'createUser', username, displayName, role, password, active });

  if (data.ok) {
    closeModal('userModal');
    toast('success', editingUserName ? 'อัปเดตผู้ใช้แล้ว' : 'เพิ่มผู้ใช้แล้ว');
    await loadUsers();
  } else {
    errEl.textContent = data.error ?? 'บันทึกไม่สำเร็จ';
  }
}
